import { graphqlClient } from '@/lib/graphql-client';
import { omitScopeFields } from '@/lib/scope-stamp';
import type { PmForm } from '@/modules/performance/hr-form-creation/types/form.types';
import {
  buildGetPmFormCandidatesQuery,
  buildGetPmReviewDetailsFullQuery,
  buildGetPmReviewDetailsQuery,
  buildGetPmReviewsQuery,
} from '../graphql/queries';
import {
  DELETE_PM_REVIEW_DETAIL_MUTATION,
  INSERT_PM_REVIEW_DETAIL_MUTATION,
  UPDATE_PM_REVIEW_DETAIL_MUTATION,
  UPDATE_PM_REVIEW_MUTATION,
} from '../graphql/mutations';
import { PagingResult, PmReview, PmReviewDetail, QueryParams } from '../types/review-core.types';

const byId = (itemId: string) => JSON.stringify({ _id: itemId });
const byIdFilter = (itemId: string): Record<string, unknown> => ({ ItemId: itemId });

/** Upper bound for "all rows of one review" reads: a review has a handful of reviewers, never hundreds. */
const REVIEW_SCOPED_PAGE_SIZE = 200;

/** Sorted on the server, by `LastUpdatedDate` descending unless `order` says otherwise. */
export const getPmReviews = async (params: QueryParams): Promise<PagingResult<PmReview>> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  const result = await graphqlClient.query<{ PmReviews: PagingResult<PmReview> }>({
    query: buildGetPmReviewsQuery(filter, pageNo, pageSize, order),
  });
  return result.PmReviews;
};

export const getPmReviewById = async (itemId: string): Promise<PmReview | null> => {
  const page = await getPmReviews({ pageNo: 1, pageSize: 1, filter: byIdFilter(itemId) });
  return page.items[0] ?? null;
};

export const getPmReviewsByIds = async (ids: string[]): Promise<PmReview[]> => {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return [];
  const page = await getPmReviews({
    pageNo: 1,
    pageSize: unique.length,
    filter: { ItemId: { $in: unique } },
  });
  return page.items;
};

export const getPmReviewDetails = async (
  params: QueryParams
): Promise<PagingResult<PmReviewDetail>> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  const result = await graphqlClient.query<{ PmReviewDetails: PagingResult<PmReviewDetail> }>({
    query: buildGetPmReviewDetailsQuery(filter, pageNo, pageSize, order),
  });
  return result.PmReviewDetails;
};

export const getPmReviewDetailById = async (itemId: string): Promise<PmReviewDetail | null> => {
  const result = await graphqlClient.query<{ PmReviewDetails: PagingResult<PmReviewDetail> }>({
    query: buildGetPmReviewDetailsFullQuery(byIdFilter(itemId), 1, 1),
  });
  return result.PmReviewDetails.items[0] ?? null;
};

/** Every reviewer task of one review (optionally one review type), without the form snapshots. All of them fit in
 * one page, so they are re-sorted client-side by `CreatedDate` ascending (oldest request first). */
export const getPmReviewDetailsForReview = async (
  pmReviewId: string,
  reviewType?: string
): Promise<PmReviewDetail[]> => {
  const filter = { PmReviewId: pmReviewId, ...(reviewType ? { ReviewType: reviewType } : {}) };
  const page = await getPmReviewDetails({ pageNo: 1, pageSize: REVIEW_SCOPED_PAGE_SIZE, filter });
  return [...page.items].sort(
    (a, b) => new Date(a.CreatedDate ?? 0).getTime() - new Date(b.CreatedDate ?? 0).getTime()
  );
};

/** Completed reviewer tasks of one review including their answered forms - the dashboard's qualitative source. */
export const getCompletedPmReviewDetailsWithForms = async (
  pmReviewId: string
): Promise<PmReviewDetail[]> => {
  const result = await graphqlClient.query<{ PmReviewDetails: PagingResult<PmReviewDetail> }>({
    query: buildGetPmReviewDetailsFullQuery(
      { PmReviewId: pmReviewId, Status: 'Completed' },
      1,
      REVIEW_SCOPED_PAGE_SIZE
    ),
  });
  return result.PmReviewDetails.items;
};

/** Legacy GetPmFormCandidates: published (not deleted) forms of one assessment + review type (+ peer category). */
export const getPmFormCandidates = async (
  assessmentType: string,
  reviewType: string,
  category?: string
): Promise<PmForm[]> => {
  const filter = {
    AssessmentType: assessmentType,
    ReviewType: reviewType,
    ...(category ? { Category: category } : {}),
  };
  const result = await graphqlClient.query<{ PmForms: { items: PmForm[] } }>({
    query: buildGetPmFormCandidatesQuery(filter, 1, REVIEW_SCOPED_PAGE_SIZE),
  });
  return result.PmForms.items;
};

export const getPmFormById = async (itemId: string): Promise<PmForm | null> => {
  const result = await graphqlClient.query<{ PmForms: { items: PmForm[] } }>({
    query: buildGetPmFormCandidatesQuery(byIdFilter(itemId), 1, 1),
  });
  return result.PmForms.items[0] ?? null;
};

export const updatePmReview = (itemId: string, input: Partial<Record<string, unknown>>) =>
  graphqlClient.mutate({
    query: UPDATE_PM_REVIEW_MUTATION,
    variables: { filter: byId(itemId), input: omitScopeFields(input) },
  });

export const insertPmReviewDetail = (input: Partial<Record<string, unknown>>) =>
  graphqlClient.mutate({ query: INSERT_PM_REVIEW_DETAIL_MUTATION, variables: { input } });

export const updatePmReviewDetail = (itemId: string, input: Partial<Record<string, unknown>>) =>
  graphqlClient.mutate({
    query: UPDATE_PM_REVIEW_DETAIL_MUTATION,
    variables: { filter: byId(itemId), input: omitScopeFields(input) },
  });

/** Removing a requested peer is a hard delete (legacy DeleteAsync): the request never happened for the reviewer. */
export const deletePmReviewDetail = (itemId: string) =>
  graphqlClient.mutate({
    query: DELETE_PM_REVIEW_DETAIL_MUTATION,
    variables: { filter: byId(itemId), input: { isHardDelete: true } },
  });
